import { tt } from '../i18n'

/**
 * What a step-up confirmation is *for*, and therefore where it posts.
 *
 * `EmployeeAuthModal` looks like one dialog but drives four different
 * endpoints, and the legacy component decided between them inline in its submit
 * handler. Pulled out here because picking the wrong one is not a cosmetic bug:
 * `return-to-kitchen` moves a driver's wallet credit (BACKEND.md §14.5) and
 * `fiskal/storno` voids a fiscal receipt. Both are pinned by tests.
 */

export interface AuthActionMeta {
  orderId: number
  orderNumber: string
  /** In cents — the modal only displays it. */
  orderTotalCents: number
  /** The status being set; also the title lookup key. */
  newStatus?: string
  /** Overrides the title when the action is not a status change. */
  customTitle?: string
  /** Suppresses the reason field, for flows the server does not record one for. */
  noReason?: boolean
  /** Re-open a finished order back into the kitchen. */
  returnToKitchen?: boolean
  /** Set when the caller will PATCH the payment method after credentials pass. */
  newPaymentMethod?: string
  previousPaymentMethod?: string
  /** Set when the caller will PATCH a non-protected status after they pass. */
  useDirectPatch?: boolean
}

export type AuthActionKind =
  | 'verify-only'
  | 'fiskal-storno'
  | 'return-to-kitchen'
  | 'confirm-status'

/**
 * The order of these checks is the legacy order and it matters. `verifyOnly`
 * wins over everything: it means the caller has already decided to make the
 * change itself and wants the credentials checked, nothing more. Were
 * `returnToKitchen` tested first, a verify-only call would re-open the order
 * *and* the caller would then PATCH it — the wallet deduction would happen for
 * an action nobody asked for.
 */
export function authActionKind(meta: AuthActionMeta, verifyOnly?: boolean): AuthActionKind {
  if (verifyOnly) return 'verify-only'
  if (meta.newStatus === FISKAL_STORNO) return 'fiskal-storno'
  if (meta.returnToKitchen) return 'return-to-kitchen'
  return 'confirm-status'
}

/** The sentinel legacy puts in `newStatus` to mean "void the receipt". */
export const FISKAL_STORNO = 'fiskal_storno'

/**
 * A reason is mandatory except for a fiscal storno — where the receipt number
 * and the confirming employee are the record — and where the caller has said it
 * will not be sent.
 */
export function authActionNeedsReason(meta: AuthActionMeta): boolean {
  return meta.newStatus !== FISKAL_STORNO && !meta.noReason
}

/** Which roles the modal tells the user may confirm this. Display only. */
export const ROLES_THAT_MAY_CONFIRM: Record<string, string[]> = {
  delivered: ['main_admin', 'admin', 'waiter', 'driver'],
  quality_check: ['main_admin', 'admin', 'cook'],
  cancelled: ['main_admin', 'admin'],
  [FISKAL_STORNO]: ['main_admin', 'admin'],
}

const STEP_UP_STATUS_LABELS: Record<string, string> = {
  delivered: tt('Doručená'),
  quality_check: tt('Kontrola kvality'),
  cancelled: tt('Zrušená'),
  [FISKAL_STORNO]: tt('Storno fiskálneho dokladu'),
}

export function authActionTitle(meta: AuthActionMeta): string {
  if (meta.returnToKitchen) return tt('Vrátiť do kuchyne')
  if (meta.customTitle) return meta.customTitle
  if (!meta.newStatus) return tt('Overenie')
  return STEP_UP_STATUS_LABELS[meta.newStatus] ?? meta.newStatus
}

export type AuthActionTone = 'destructive' | 'quality' | 'reopen' | 'confirm'

export function authActionTone(meta: AuthActionMeta): AuthActionTone {
  if (meta.returnToKitchen) return 'reopen'
  if (meta.newStatus === 'cancelled' || meta.newStatus === FISKAL_STORNO) return 'destructive'
  if (meta.newStatus === 'quality_check') return 'quality'
  return 'confirm'
}

/** The reason field asks a different question for each action. */
export function reasonLabel(meta: AuthActionMeta): string {
  const tone = authActionTone(meta)
  if (tone === 'reopen') return tt('Dôvod vrátenia *')
  if (tone === 'destructive') return tt('Dôvod storna *')
  if (tone === 'quality') return tt('Poznámka ku kontrole *')
  return tt('Dôvod doručenia *')
}

export function reasonPlaceholder(meta: AuthActionMeta): string {
  const tone = authActionTone(meta)
  if (tone === 'reopen') return tt('Napr. zlá adresa, reklamácia, znovu pripraviť...')
  if (tone === 'destructive') return tt('Zadajte dôvod storna...')
  if (tone === 'quality') return tt('Popis problému alebo kontroly...')
  return tt('Zadajte dôvod doručenia...')
}
